/**
 * SEO and JSON-LD for the vendor market categories hub (`/vendor-market`).
 * Aligns with `place/index.seo.ts`: absolute URLs, robots by environment, and a JSON-LD array
 * (`CollectionPage` + `BreadcrumbList`) for richer indexing.
 */

import adultProductsImage from '@web/src/assets/vendor-market/vm-adult-products.webp?w=1200&format=webp'
import allVendorsImage from '@web/src/assets/vendor-market/vm-all.webp?w=1200&format=webp'
import apparelImage from '@web/src/assets/vendor-market/vm-apparel.webp?w=1200&format=webp'
import automotiveImage from '@web/src/assets/vendor-market/vm-automotive.webp?w=1200&format=webp'
import beautyImage from '@web/src/assets/vendor-market/vm-beauty.webp?w=1200&format=webp'
import entertainmentImage from '@web/src/assets/vendor-market/vm-entertainment.webp?w=1200&format=webp'
import eventsImage from '@web/src/assets/vendor-market/vm-events.webp?w=1200&format=webp'
import furnitureImage from '@web/src/assets/vendor-market/vm-furniture.webp?w=1200&format=webp'
import generalLaborImage from '@web/src/assets/vendor-market/vm-general-labor.webp?w=1200&format=webp'
import healthImage from '@web/src/assets/vendor-market/vm-health.webp?w=1200&format=webp'
import mediaImage from '@web/src/assets/vendor-market/vm-media.webp?w=1200&format=webp'
import otherImage from '@web/src/assets/vendor-market/vm-other.webp?w=1200&format=webp'
import professionalServicesImage from '@web/src/assets/vendor-market/vm-professional-services.webp?w=1200&format=webp'
import tradesImage from '@web/src/assets/vendor-market/vm-trades.webp?w=1200&format=webp'
import wellnessImage from '@web/src/assets/vendor-market/vm-wellness.webp?w=1200&format=webp'
import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { SEO_META_DESCRIPTION_MAX_CHARS, truncateMetaDescription } from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath } from '@web/src/lib/utilsClient'
import type { VendorMarketCategoryEnum } from 'types/graphql'

export type VendorMarketCategoryCountsForSeo = {
  allVendors: number
  adultProducts: number
  apparel: number
  automotive: number
  beauty: number
  entertainment: number
  events: number
  furniture: number
  generalLabor: number
  health: number
  media: number
  other: number
  professionalServices: number
  trades: number
  wellness: number
}

export type VendorMarketCategoriesIndexSeoLoaderInput = {
  vendorMarketCategoryCounts?: VendorMarketCategoryCountsForSeo | null
}

type VmTileSeoConfig = {
  titleKey: string
  imagePath: string
  countKey: keyof VendorMarketCategoryCountsForSeo
  category?: VendorMarketCategoryEnum
}

/** Same tiles as `index.tsx` cards — used only for structured data URLs and labels. */
const CATEGORY_TILES_FOR_JSON_LD: VmTileSeoConfig[] = [
  { titleKey: 'All Vendors', imagePath: allVendorsImage, countKey: 'allVendors' },
  { titleKey: 'Apparel', imagePath: apparelImage, countKey: 'apparel', category: 'APPAREL' },
  {
    titleKey: 'Adult products',
    imagePath: adultProductsImage,
    countKey: 'adultProducts',
    category: 'ADULT_PRODUCTS',
  },
  { titleKey: 'Automotive', imagePath: automotiveImage, countKey: 'automotive', category: 'AUTOMOTIVE' },
  { titleKey: 'Beauty', imagePath: beautyImage, countKey: 'beauty', category: 'BEAUTY' },
  {
    titleKey: 'Entertainment',
    imagePath: entertainmentImage,
    countKey: 'entertainment',
    category: 'ENTERTAINMENT',
  },
  { titleKey: 'Events', imagePath: eventsImage, countKey: 'events', category: 'EVENTS' },
  { titleKey: 'Furniture', imagePath: furnitureImage, countKey: 'furniture', category: 'FURNITURE' },
  {
    titleKey: 'General labor',
    imagePath: generalLaborImage,
    countKey: 'generalLabor',
    category: 'GENERAL_LABOR',
  },
  { titleKey: 'Health', imagePath: healthImage, countKey: 'health', category: 'HEALTH' },
  { titleKey: 'Media', imagePath: mediaImage, countKey: 'media', category: 'MEDIA' },
  { titleKey: 'Trades', imagePath: tradesImage, countKey: 'trades', category: 'TRADES' },
  {
    titleKey: 'Professional services',
    imagePath: professionalServicesImage,
    countKey: 'professionalServices',
    category: 'PROFESSIONAL_SERVICES',
  },
  { titleKey: 'Wellness', imagePath: wellnessImage, countKey: 'wellness', category: 'WELLNESS' },
  { titleKey: 'Other', imagePath: otherImage, countKey: 'other', category: 'OTHER' },
]

/** Discover list URL with optional category filter (matches card navigation to `/vendor-market/list`). */
function absoluteDiscoverVendorMarketListUrl(category?: VendorMarketCategoryEnum): string {
  const base = absoluteUrlForPath('/vendor-market/list')
  const u = new URL(base)
  u.searchParams.set('page', '1')
  if (category) {
    u.searchParams.append('categories', category)
  }
  return u.href
}

function countForTile(tile: VmTileSeoConfig, counts: VendorMarketCategoryCountsForSeo | null | undefined): number {
  if (!counts) return 0
  return counts[tile.countKey] ?? 0
}

function buildVendorMarketCategoriesBreadcrumbJsonLd(pageUrl: string, tr: typeof i18n.t): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: tr('Home'), item: absoluteUrlForPath('/') },
      {
        '@type': 'ListItem',
        position: 2,
        name: tr('Vendor Market'),
        item: pageUrl,
      },
    ],
  }
}

function buildVendorMarketCategoriesCollectionJsonLd(input: {
  tr: typeof i18n.t
  pageUrl: string
  pageTitle: string
  pageDescription: string
  counts: VendorMarketCategoryCountsForSeo | null | undefined
}): Record<string, unknown> {
  const { tr, pageUrl, pageTitle, pageDescription, counts } = input

  const itemListElement = CATEGORY_TILES_FOR_JSON_LD.map((tile, index) => {
    const title = tr(tile.titleKey)
    const listUrl = absoluteDiscoverVendorMarketListUrl(tile.category)
    const imageUrl = absoluteUrlForPath(tile.imagePath)
    const n = countForTile(tile, counts)
    const description = n > 0 ? tr('{{count}} Vendors', { count: n }) : undefined

    return {
      '@type': 'ListItem',
      position: index + 1,
      name: title,
      item: {
        '@type': 'WebPage',
        name: title,
        url: listUrl,
        image: imageUrl,
        ...(description ? { description } : {}),
      },
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: pageUrl,
    image: absoluteUrlForPath('/logo-og-image.webp'),
    mainEntity: {
      '@type': 'ItemList',
      name: tr('Vendor categories'),
      numberOfItems: CATEGORY_TILES_FOR_JSON_LD.length,
      itemListElement,
    },
  }
}

function buildMetaDescription(tr: typeof i18n.t, counts: VendorMarketCategoryCountsForSeo | null | undefined): string {
  const baseKey =
    'Explore vendor categories on SpicyFriends—trades, wellness, apparel, events, and services. Filter by category for lifestyle-friendly pros that match your needs.'
  const withCountKey =
    'Explore {{count}} SpicyFriends vendors across trades, wellness, apparel, events, and more. Filter by category for lifestyle-friendly pros that fit your needs.'

  const base = tr(baseKey)
  if (counts != null && counts.allVendors > 0) {
    const withCount = tr(withCountKey, { count: counts.allVendors })
    if (withCount.length <= SEO_META_DESCRIPTION_MAX_CHARS) return withCount
    return truncateMetaDescription(withCount, SEO_META_DESCRIPTION_MAX_CHARS)
  }
  if (base.length <= SEO_META_DESCRIPTION_MAX_CHARS) return base
  return truncateMetaDescription(base, SEO_META_DESCRIPTION_MAX_CHARS)
}

export function vendorMarketCategoriesIndexHeadFromLoaderData(
  loaderData: VendorMarketCategoriesIndexSeoLoaderInput | null | undefined
) {
  const tr = i18n.t.bind(i18n)
  const counts = loaderData?.vendorMarketCategoryCounts ?? undefined
  const pageUrl = absoluteUrlForPath('/vendor-market')
  const pageTitle = tr('Find Your Vendor')
  const pageDescription = buildMetaDescription(tr, counts)

  const collectionPage = buildVendorMarketCategoriesCollectionJsonLd({
    tr,
    pageUrl,
    pageTitle,
    pageDescription,
    counts,
  })
  const breadcrumbList = buildVendorMarketCategoriesBreadcrumbJsonLd(pageUrl, tr)

  return seo({
    title: `${pageTitle} | ${env.VITE_APP_NAME}`,
    description: pageDescription,
    url: pageUrl,
    image: '/logo-og-image.webp',
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: [collectionPage, breadcrumbList],
  })
}
